import {
  ArticleType,
  MetricsGroupBy,
  ReportSection,
} from 'app/modules/reportDashboards/models';
import { YAxisType } from 'app/modules/reportDashboards/enums';

export const MAX_ITEMS_IN_CHART_TOOLTIP = 10;

// Defines chart config for each section + initial values (enabled, chartType)
export const DEFAULT_REPORT_CHARTS: ReportSection[] = [
  {
    id: 'kpis',
    title: 'KPIs',
    stats: [
      {
        type: 'alert-to-case',
      },
    ],
    charts: [],
  },
  {
    id: 'alerts',
    title: 'Alerts',
    stats: [
      {
        type: 'total-alerts',
      },
      {
        type: 'total-alerts-closed',
      },
      {
        type: 'total-alerts-dispositioned',
      },
    ],
    charts: [
      {
        id: 'alertsGeneratedOverTime',
        type: 'alert-creation',
        title: 'Total Alerts Generated Over Time',
        enabled: true,
        chartType: 'line',
      },
      {
        id: 'alertCreationPerRule',
        type: 'alert-creation',
        title: 'Alerts Generated per Rule',
        enabled: false,
        chartType: 'bar',
        groupBy: 'rule',
      },
      {
        id: 'alertCreationPerQueue',
        type: 'alert-creation',
        title: 'Alerts Generated per Queue',
        enabled: true,
        chartType: 'bar',
        groupBy: 'queue',
      },
      {
        id: 'alertDispositionPerDisposition',
        type: 'alert-action',
        title: 'Alerts Dispositioned per Disposition',
        enabled: true,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'disposition',
      },
      {
        id: 'alertDispositionPerAgent',
        type: 'alert-action',
        title: 'Alert Disposition per Agent',
        enabled: false,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'agent',
      },
      {
        id: 'alertClosedPerQueue',
        type: 'alert-action',
        title: 'Alert Closed per Queue',
        enabled: false,
        chartType: 'bar',
        action: 'closed',
        groupBy: 'queue',
      },
      {
        id: 'alertClosedPerAgent',
        type: 'alert-action',
        title: 'Alert Closed per Agent',
        enabled: true,
        chartType: 'bar',
        action: 'closed',
        groupBy: 'agent',
      },
      {
        id: 'alertAvgResolutionTimePerAgent',
        type: 'alert-action-duration',
        title: 'Alert Average Resolution Time per Agent',
        enabled: true,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'agent',
        durationMetric: 'avg_resolution_time',
      },
    ],
  },
  {
    id: 'cases',
    title: 'Cases',
    stats: [
      {
        type: 'total-cases',
      },
      {
        type: 'total-cases-dispositioned',
      },
    ],
    charts: [
      {
        id: 'caseCreationPerQueue',
        type: 'case-creation',
        title: 'Cases Created per Queue',
        enabled: true,
        chartType: 'bar',
        groupBy: 'queue',
      },
      {
        id: 'caseDispositionPerDisposition',
        type: 'case-action',
        title: 'Cases Dispositioned per Disposition',
        enabled: true,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'disposition',
      },
      {
        id: 'caseDispositionPerAgent',
        type: 'case-action',
        title: 'Cases Dispositioned per Agent',
        enabled: false,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'agent',
      },
      {
        id: 'caseClosedPerQueue',
        type: 'case-action',
        title: 'Cases Closed per Queue',
        enabled: false,
        chartType: 'bar',
        action: 'closed',
        groupBy: 'queue',
      },
      {
        id: 'caseClosedPerAgent',
        type: 'case-action',
        title: 'Cases Closed per Agent',
        enabled: true,
        chartType: 'bar',
        action: 'closed',
        groupBy: 'agent',
      },
      {
        id: 'caseAvgResolutionTimePerAgent',
        type: 'case-action-duration',
        title: 'Case Average Resolution Time per Agent',
        enabled: true,
        chartType: 'bar',
        action: 'dispositioned',
        groupBy: 'agent',
        durationMetric: 'avg_resolution_time',
      },
    ],
  },
  {
    id: 'sars',
    title: 'SARs',
    stats: [
      {
        type: 'total-sars',
      },
      {
        type: 'total-sars-completed-on-time',
      },
      {
        type: 'total-sars-failed-or-rejected',
      },
    ],
    charts: [
      {
        id: 'sarCreationOverTime',
        type: 'sar-creation',
        title: 'SARs Created Over Time',
        enabled: true,
        chartType: 'bar',
      },
      {
        id: 'sarCreationPerAgent',
        type: 'sar-creation',
        title: 'SARs Created per Agent',
        enabled: true,
        chartType: 'bar',
        groupBy: 'agent',
      },
      {
        id: 'sarCompletedPerAgent',
        type: 'sar-action',
        title: 'SARs Completed per Agent',
        enabled: true,
        chartType: 'bar',
        action: 'submitted',
        groupBy: 'agent',
      },
      {
        id: 'sarAvgResolutionTimePerAgent',
        type: 'sar-action-duration',
        title: 'Average Time Spent on SAR per Agent',
        enabled: true,
        action: 'submitted',
        groupBy: 'agent',
        durationMetric: 'avg_resolution_time',
      },
    ],
  },
];

export const ARTICLE_LABEL: Record<ArticleType, string> = {
  alert: 'Alert',
  case: 'Case',
  sar: 'SAR',
};

export const X_AXIS_LABEL: Record<MetricsGroupBy, string> = {
  agent: 'Agents',
  queue: 'Queues',
  rule: 'Rules',
  disposition: 'Dispositions',
  type: 'Types',
};

export const Y_AXIS_LABEL: Record<YAxisType, string> = {
  [YAxisType.IDS]: 'Count',
  [YAxisType.TIME]: 'Hours',
};
